/**
 * preview-channel — the pure postMessage contract with the embedded pop.in app,
 * extracted from the React frame so the v2/legacy decision is unit-testable.
 *
 * Two channels:
 *   • legacy `popin:theme-preview` — the 6 pre-computed hexes (+ logo/terminology).
 *   • v2 `popin:theme-preview-v2` — the RECIPE (treatment/seed/polarity/font); a
 *     v2-aware embed regenerates the full theme with the real engine.
 *
 * Graceful fallback: until an embed ACKs v2 we send BOTH (v2 + legacy), so a
 * v2-unaware build still previews via the legacy message. Once it acks, v2 only.
 */

/** postMessage type tags (see popin-rn useBrandTheme; v2 listener lands in the token-map issue). */
export const PREVIEW_MESSAGE_TYPE = "popin:theme-preview";
export const PREVIEW_READY_TYPE = "popin:preview-ready";
export const PREVIEW_MESSAGE_TYPE_V2 = "popin:theme-preview-v2";
export const PREVIEW_V2_ACK_TYPE = "popin:theme-preview-v2-ack";

/** The theme RECIPE a v2-aware embed regenerates from (ids/scalars, not colours). */
export interface ThemeRecipePayload {
  treatment: string;
  seed: string;
  polarity: "light" | "dark";
  fontId: string;
}

/** The unsaved draft pushed into the embed on every edit. */
export interface ThemePreview {
  name: string;
  colors: {
    background: string;
    surface: string;
    primary: string;
    accent: string;
    text: string;
    header: string;
  };
  logoText: string;
  logoImg: string | null;
  terminology: {
    currencySingular: string;
    currencyPlural: string;
    currencyIcon: string;
    walletLabel: string;
    rewardLabel: string;
  };
  /** When set, the preview is treatment-driven (v2); absent → raw-hex/legacy only. */
  recipe?: ThemeRecipePayload;
}

export interface PreviewMessage {
  type: string;
  [key: string]: unknown;
}

/**
 * Decide which message(s) to post for a preview, given whether this embed has
 * acked v2. Pure — the frame just posts the returned messages to PLAY_ORIGIN.
 *
 *   • no recipe            → [legacy]                (raw-hex edits / terminology)
 *   • recipe, not v2-acked → [v2, legacy]            (fallback: old embed still previews)
 *   • recipe, v2-acked     → [v2]                    (embed regenerates from the recipe)
 */
export function buildPreviewMessages(
  preview: ThemePreview,
  v2Acked: boolean,
): { channel: "legacy" | "v2" | "v2+legacy"; messages: PreviewMessage[] } {
  const { recipe, ...rest } = preview;
  if (!recipe) {
    return { channel: "legacy", messages: [{ type: PREVIEW_MESSAGE_TYPE, ...rest }] };
  }
  const messages: PreviewMessage[] = [{ type: PREVIEW_MESSAGE_TYPE_V2, ...rest, ...recipe }];
  if (!v2Acked) messages.push({ type: PREVIEW_MESSAGE_TYPE, ...rest });
  return { channel: v2Acked ? "v2" : "v2+legacy", messages };
}
